export type CapabilityState =
  | 'AVAILABLE'
  | 'STALE'
  | 'NOT_REPORTED'
  | 'NOT_SUPPORTED'
  | 'UNSUPPORTED'
  | 'NOT_CONFIGURED'
  | 'NO_DATA'
  | 'LEARNING'
  | string;

import { canonicalMetricId } from '@aot-aiops/shared';
export { canonicalMetricId };

export function humanDuration(seconds: number | null | undefined) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return 'Not available';
  const totalMinutes = Math.floor(seconds / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const parts = [days ? `${days}d` : '', hours ? `${hours}h` : '', minutes || (!days && !hours) ? `${minutes}m` : ''].filter(Boolean);
  return parts.join(' ');
}

export function humanCapabilityState(state?: CapabilityState | null) {
  const labels: Record<string, string> = {
    AVAILABLE: 'Available',
    SUPPORTED_VIA_GUEST_AGENT: 'Available',
    STALE: 'Stale',
    NOT_REPORTED: 'Not reported',
    NOT_SUPPORTED: 'Not supported',
    UNSUPPORTED: 'Not supported',
    NOT_CONFIGURED: 'Not configured',
    NOT_AVAILABLE: 'Not available',
    NO_DATA: 'No data',
    LEARNING: 'Learning',
    HEALTHY_BASELINE: 'Healthy',
    ANOMALOUS: 'Anomalous',
  };
  return labels[String(state ?? '')] ?? String(state ?? 'Not reported').replaceAll('_', ' ');
}

function valueOf(metric: any) {
  const value = Number(metric?.value);
  return Number.isFinite(value) ? value : null;
}

function pointValue(point: any) {
  const value = Number(point?.avg ?? point?.latest);
  return Number.isFinite(value) ? value : null;
}

export function metricHistory(history: any[], metric: string) {
  const canonical = canonicalMetricId(metric);
  return history
    .filter((item) => canonicalMetricId(String(item.metric)) === canonical)
    .sort(
      (left, right) =>
        new Date(left.windowEnd ?? left.windowStart).getTime() -
        new Date(right.windowEnd ?? right.windowStart).getTime(),
    );
}

export function networkSummary(metrics: any[], history: any[]) {
  const metric = (id: string) =>
    metrics.find((item) => canonicalMetricId(String(item.name)) === id);
  const summarize = (id: string) => {
    const points = metricHistory(history, id);
    const values = points.map(pointValue).filter((value): value is number => value != null);
    const current = valueOf(metric(id)) ?? values.at(-1) ?? null;
    return {
      current,
      average: values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : current,
      peak: values.length ? Math.max(...values) : current,
      points,
    };
  };
  return {
    receive: summarize('network.receive.bits_per_second'),
    transmit: summarize('network.transmit.bits_per_second'),
    totalReceived: valueOf(metric('network.receive.total.bytes')),
    totalTransmitted: valueOf(metric('network.transmit.total.bytes')),
  };
}

function trendFor(points: any[]) {
  const values = points.map(pointValue).filter((value): value is number => value != null);
  if (values.length < 4) return 'Insufficient samples';
  const half = Math.floor(values.length / 2);
  const average = (items: number[]) => items.reduce((sum, value) => sum + value, 0) / items.length;
  const before = average(values.slice(0, half));
  const after = average(values.slice(-half));
  const tolerance = Math.max(Math.abs(before) * 0.05, 0.01);
  const spread = Math.max(...values) - Math.min(...values);
  if (spread > Math.max(Math.abs(before) * 0.75, tolerance * 4)) return 'Volatile';
  if (after > before + tolerance) return 'Increasing';
  if (after < before - tolerance) return 'Decreasing';
  return 'Stable';
}

function rateFor(points: any[]) {
  const usable = points
    .map((point) => ({
      value: pointValue(point),
      at: new Date(point.windowEnd ?? point.windowStart).getTime(),
    }))
    .filter((item): item is { value: number; at: number } => item.value != null && Number.isFinite(item.at));
  if (usable.length < 2) return null;
  const first = usable[0]!;
  const last = usable.at(-1)!;
  const durationMinutes = Math.round((last.at - first.at) / 60_000);
  if (durationMinutes <= 0) return null;
  return {
    perHour: ((last.value - first.value) / durationMinutes) * 60,
    percent: first.value === 0 ? null : ((last.value - first.value) / Math.abs(first.value)) * 100,
    durationMinutes,
  };
}

function behaviorEligible(metric: string) {
  const id = canonicalMetricId(metric);
  return !(
    id === 'uptime.seconds' ||
    id.endsWith('.total.bytes') ||
    id.endsWith('.capacity.bytes') ||
    id.endsWith('.allocated.bytes')
  );
}

export function behaviorMetricSummaries(input: {
  metrics: any[];
  history: any[];
  baselines: any[];
  findings: any[];
  capabilities?: any;
}) {
  const current = new Map(
    input.metrics.map((item) => [canonicalMetricId(String(item.name)), item]),
  );
  const baselines = input.baselines.filter((item) => item.bucket === 'all');
  const capability = new Map<string, any>(
    (input.capabilities?.metrics ?? []).map((item: any) => [canonicalMetricId(String(item.id)), item]),
  );
  const ids = new Set([
    ...baselines.map((item) => canonicalMetricId(String(item.metric))),
    ...input.metrics.map((item) => canonicalMetricId(String(item.name))),
  ]);
  return [...ids]
    .filter(
      (id) =>
        behaviorEligible(id) &&
        !id.endsWith('.rate_per_hour') &&
        capability.get(id)?.state !== 'NOT_AVAILABLE',
    )
    .map((id) => {
      const metric = current.get(id);
      const baseline = baselines.find((item) => canonicalMetricId(String(item.metric)) === id);
      const findings = input.findings.filter(
        (item) => canonicalMetricId(String(item.metric)) === id,
      );
      const active = findings.find((item) => item.state !== 'recovered');
      const latestFinding = [...findings].sort(
        (left, right) =>
          new Date(right.lastDetectedAt).getTime() - new Date(left.lastDetectedAt).getTime(),
      )[0];
      const points = metricHistory(input.history, id);
      const currentValue = valueOf(metric);
      const enoughBaseline = baseline && baseline.confidence !== 'cold_start';
      const status = active
        ? 'Anomalous'
        : !enoughBaseline
          ? 'Learning'
          : currentValue == null
            ? 'Not reported'
            : currentValue < Number(baseline.lowerBound) || currentValue > Number(baseline.upperBound)
              ? 'Outside expected range'
              : 'Normal';
      return {
        metric: id,
        unit: metric?.unit ?? baseline?.unit,
        currentValue,
        lowerBound: enoughBaseline ? Number(baseline.lowerBound) : null,
        upperBound: enoughBaseline ? Number(baseline.upperBound) : null,
        confidence: baseline?.confidence ?? 'cold_start',
        sampleCount: Number(baseline?.sampleCount ?? 0),
        trainedFrom: baseline?.trainedFrom ?? null,
        trainedUntil: baseline?.trainedUntil ?? null,
        deviation:
          status === 'Normal'
            ? 'Within expected range'
            : status === 'Learning'
              ? 'Learning expected range'
              : status,
        trend: trendFor(points),
        rateOfChange: rateFor(points),
        status,
        lastAnomaly: latestFinding?.lastDetectedAt ?? null,
        activeFinding: active ?? null,
        points,
      };
    })
    .sort((left, right) => {
      const order: Record<string, number> = { Anomalous: 0, 'Outside expected range': 1, Normal: 2, Learning: 3, 'Not reported': 4 };
      return (order[left.status] ?? 5) - (order[right.status] ?? 5) || left.metric.localeCompare(right.metric);
    });
}

export function baselineMaturity(rows: ReturnType<typeof behaviorMetricSummaries>) {
  const rank: Record<string, number> = { cold_start: 0, low: 1, medium: 2, high: 3 };
  const maximum = rows.reduce((value, row) => Math.max(value, rank[row.confidence] ?? 0), 0);
  const labels = ['Learning', 'Low confidence', 'Medium confidence', 'High confidence'];
  const historyDays = rows.reduce((days, row) => {
    if (!row.trainedFrom || !row.trainedUntil) return days;
    return Math.max(days, Math.floor((Date.parse(row.trainedUntil) - Date.parse(row.trainedFrom)) / 86_400_000));
  }, 0);
  return { label: labels[maximum]!, historyDays };
}
